/**
 * Music-lib bridge tools. Each tool exposes a focused piece of `@tunasong/music-lib`
 * (chord parsing, scale theory, diatonic analysis, secondary dominants, tritone
 * subs, progression mood-summarization) to the agent.
 *
 * Design principles (see `2026-05-15-agent-tools-api-design.md` §4b):
 *   - Inputs are LLM-friendly strings (`chord: "Cmaj7"`, `key: "F lydian"`).
 *   - Outputs are JSON-friendly primitives — never raw `Chord` / `Scale` objects.
 *   - Each tool is one verb on one noun. The LLM composes them.
 *   - Pure functions only: no I/O, no async work that would warrant streaming.
 *
 * Key-string parsing is permissive on purpose. The LLM may emit `"C"`,
 * `"C major"`, `"c-major"`, or `"F Lydian"` — we normalize whitespace, case,
 * and hyphenation before looking up the `ScaleType`. Unknown roots or types
 * surface a clear error rather than silently coercing.
 */
import {
  ChordLib,
  ScaleLib,
  analyzeChord,
  getDiatonicFunction,
  getSecondaryDominantFunction,
  getTritoneSubFunction,
  isChordVariant,
  summarizeProgression,
} from '@tunasong/music-lib'
import type { Chord, ChordAnalysis, ChordFunction, ChordVariant } from '@tunasong/music-lib'
import { scaleNames } from '@tunasong/schemas'
import type { NoteName, Scale, ScaleType } from '@tunasong/schemas'
import { z } from 'zod'
import type { AnyTool, Tool } from '../types.js'

// ───────────────────────────────────────────────────────────────────────────────
// Parse helpers — turn LLM-supplied strings into music-lib types.
// ───────────────────────────────────────────────────────────────────────────────

const SCALE_TYPE_SET = new Set<string>(scaleNames)

const parseChord = (name: string): Chord | undefined => ChordLib.fromName(name.trim())

const NOTE_NAME_PATTERN = /^[A-Ga-g](?:bb|##|b|#)?$/

const normalizeNoteName = (raw: string): NoteName | undefined => {
  const trimmed = raw.trim()
  if (!NOTE_NAME_PATTERN.test(trimmed)) {
    return undefined
  }
  // Capitalize the letter; preserve accidentals as-typed (`b`/`#`/`bb`/`##`).
  return (trimmed[0].toUpperCase() + trimmed.slice(1)) as NoteName
}

const normalizeScaleType = (raw: string | undefined): ScaleType | undefined => {
  if (!raw) {
    return 'major'
  }
  const lower = raw.trim().toLowerCase().replace(/[\s_-]+/g, '')
  return SCALE_TYPE_SET.has(lower) ? (lower as ScaleType) : undefined
}

/**
 * Parse a key/scale string like `"C major"`, `"F lydian"`, `"Bb minor"`,
 * or just `"D"` (defaults to major) into a Scale. Returns `undefined` when
 * the root or scale-type cannot be resolved.
 */
const parseKey = (key: string): Scale | undefined => {
  const tokens = key.trim().split(/\s+/)
  if (tokens.length === 0) {
    return undefined
  }
  const root = normalizeNoteName(tokens[0])
  if (!root) {
    return undefined
  }
  const type = normalizeScaleType(tokens.slice(1).join(' '))
  if (!type) {
    return undefined
  }
  return { root, type }
}

// ───────────────────────────────────────────────────────────────────────────────
// Key inference — derive a Scale from a chord progression when the user
// hasn't named one. Threshold is intentionally strict (≥0.8) so we don't
// confidently analyze against a wrong key.
// ───────────────────────────────────────────────────────────────────────────────

const MIN_KEY_INFERENCE_CONFIDENCE = 0.8

const round2 = (n: number): number => Math.round(n * 100) / 100

interface KeyInference {
  scale: Scale
  keyName: string
  confidence: number
  /** Next-best candidates (up to 3). Useful to surface when confidence is borderline. */
  alternates: { key: string; confidence: number }[]
}

const inferKeyFromChords = (chords: Chord[]): KeyInference | null => {
  if (chords.length < 2) {
    return null
  }
  const matches = ScaleLib.scaleMatchChords(chords)
  if (matches.length === 0) {
    return null
  }
  const top = matches[0]
  const alternates = matches.slice(1, 4).map(m => ({
    key: ScaleLib.getName(m.scale),
    confidence: round2(m.score),
  }))
  return {
    scale: top.scale,
    keyName: ScaleLib.getName(top.scale),
    confidence: round2(top.score),
    alternates,
  }
}

// ───────────────────────────────────────────────────────────────────────────────
// Projection helpers — turn music-lib types into LLM-friendly primitives.
// ───────────────────────────────────────────────────────────────────────────────

/**
 * Functional quality categories. Separate from `variant` so the LLM doesn't
 * have to memorize the chord-variant grammar to ask "is this chord minor?".
 * `'half-diminished'` is split from `'diminished'` because the two behave
 * very differently in voice leading (half-dim = ii° in minor cadences;
 * fully-diminished = passing/leading-tone).
 */
type ChordQuality =
  | 'major'
  | 'minor'
  | 'dominant'
  | 'diminished'
  | 'half-diminished'
  | 'augmented'
  | 'sus'
  | 'power'

/** Functional seventh categories. `'none'` when the chord is a triad / power / sus / add. */
type ChordSeventh = 'none' | 'maj7' | '7' | 'm7' | 'dim7' | 'm7b5' | 'mmaj7'

const QUALITY_BY_VARIANT: Record<string, ChordQuality> = {
  '': 'major',
  M: 'major',
  '6': 'major',
  maj7: 'major',
  Δ: 'major',
  M7: 'major',
  add9: 'major',
  add11: 'major',
  m: 'minor',
  m6: 'minor',
  m7: 'minor',
  mmaj7: 'minor',
  mb5: 'minor',
  mb6: 'minor',
  '7': 'dominant',
  '9': 'dominant',
  '7b5': 'dominant',
  '7#5': 'dominant',
  '7#9': 'dominant',
  '7b9': 'dominant',
  '7sus2': 'dominant',
  '7sus4': 'dominant',
  '7sus2sus4': 'dominant',
  aug7: 'dominant',
  '+7': 'dominant',
  dim: 'diminished',
  dim6: 'diminished',
  dim7: 'diminished',
  dimb5: 'diminished',
  dimb6: 'diminished',
  dimmaj7: 'diminished',
  m7b5: 'half-diminished',
  ø: 'half-diminished',
  ø7: 'half-diminished',
  min7b5: 'half-diminished',
  aug: 'augmented',
  augb5: 'augmented',
  augb6: 'augmented',
  aug6: 'augmented',
  augmaj7: 'augmented',
  sus2: 'sus',
  sus4: 'sus',
  sus2sus4: 'sus',
  '5': 'power',
  b5: 'major',
  b6: 'major',
}

const SEVENTH_BY_VARIANT: Record<string, ChordSeventh> = {
  maj7: 'maj7',
  Δ: 'maj7',
  M7: 'maj7',
  augmaj7: 'maj7',
  '7': '7',
  '9': '7',
  '7b5': '7',
  '7#5': '7',
  '7#9': '7',
  '7b9': '7',
  '7sus2': '7',
  '7sus4': '7',
  '7sus2sus4': '7',
  aug7: '7',
  '+7': '7',
  m7: 'm7',
  dim7: 'dim7',
  m7b5: 'm7b5',
  ø: 'm7b5',
  ø7: 'm7b5',
  min7b5: 'm7b5',
  mmaj7: 'mmaj7',
  dimmaj7: 'mmaj7',
}

const qualityOf = (variant: string): ChordQuality => QUALITY_BY_VARIANT[variant] ?? 'major'
const seventhOf = (variant: string): ChordSeventh => SEVENTH_BY_VARIANT[variant] ?? 'none'

interface ChordProjection {
  root: NoteName
  variant: string
  /**
   * High-level chord quality. Derived from `variant` so the LLM doesn't have to
   * memorize the variant grammar. Critical for disambiguating `m7b5`
   * (half-diminished — a ii° in minor cadences) from `m7` (regular minor).
   */
  quality: ChordQuality
  /** Functional seventh, or 'none' for triads / power / sus / add chords. */
  seventh: ChordSeventh
  bassNote?: NoteName
  name: string
  notes: NoteName[]
}

const projectChord = (chord: Chord | undefined): ChordProjection | null => {
  if (!chord || !isChordVariant(chord)) {
    return null
  }
  const variant: ChordVariant = chord
  return {
    root: variant.root,
    variant: variant.variant,
    quality: qualityOf(variant.variant),
    seventh: seventhOf(variant.variant),
    bassNote: variant.bassNote,
    name: ChordLib.getName(chord),
    notes: ChordLib.getNotes(variant),
  }
}

// ───────────────────────────────────────────────────────────────────────────────
// Argument schemas — strings in, strings out.
// ───────────────────────────────────────────────────────────────────────────────

const chordNameArg = z
  .string()
  .min(1)
  .describe('Chord name. Examples: "C", "Am7", "G7", "F#dim", "Cmaj7", "Bbsus4/D".')

const keyArg = z
  .string()
  .min(1)
  .describe(
    'Key/scale string. Format: "<root> <type>" (e.g. "C major", "F lydian", "Bb minor"). Type defaults to "major" if omitted ("D" → D major).'
  )

const noteNameArrayArg = z
  .array(z.string().min(1))
  .min(1)
  .describe('Note names. Examples: ["C", "E", "G"] or ["C4", "E4", "G4"] with octave for bass-note detection.')

// ───────────────────────────────────────────────────────────────────────────────
// Tools
// ───────────────────────────────────────────────────────────────────────────────

// 1. analyze_chord — chord function + borrowing + confidence against a key.
// Accepts either an explicit `key` or a `progression` (≥2 chords) to infer
// the key from at ≥0.8 confidence.
const analyzeChordSchema = z
  .object({
    chord: chordNameArg,
    key: keyArg.optional(),
    progression: z
      .array(chordNameArg)
      .min(2)
      .describe(
        'Other chords from the same song/section, used to infer the key when `key` is not supplied. Inference must clear 0.8 confidence; otherwise the tool returns an error listing the top candidate keys so you can ask the user to confirm.'
      )
      .optional(),
  })
  .refine(d => d.key !== undefined || (d.progression !== undefined && d.progression.length >= 2), {
    message: 'analyze_chord requires either `key` or `progression` (≥2 chords).',
  })

interface AnalyzeChordResult {
  /** The chord's analysis against the chosen key. `null` when music-lib cannot analyze it. */
  analysis: ChordAnalysis | null
  /** The key the analysis used (e.g., "C major"). Always populated — supplied OR inferred. */
  analyzedKey: string
  /**
   * Present only when the key was inferred from `progression`. Carries the
   * confidence score (0..1) and the next-best alternates so the agent can
   * report the inferred key to the user and check if it matches their intent.
   */
  keyInference?: {
    confidence: number
    alternates: { key: string; confidence: number }[]
  }
}

export const createAnalyzeChordTool = (): Tool<z.infer<typeof analyzeChordSchema>, AnalyzeChordResult> => ({
  name: 'analyze_chord',
  description:
    'Analyze a chord against a key. Returns its diatonic function (e.g., "I", "ii", "V", "vii°"), any borrowing (parallel-mode, secondary-dominant), a one-line description, and a confidence (high/medium/low). Supply either `key` explicitly OR `progression` (≥2 chords from the same song/section) and the tool will infer the key. Inferred keys must clear 0.8 confidence; below that, the tool returns an error with the top candidates so you can ask the user. The result always names the key actually used via `analyzedKey`, and surfaces `keyInference.confidence` + alternates when the key was inferred. If you don\'t have a chord in the user message or song context, ASK the user — do not call this tool with empty arguments.',
  schema: analyzeChordSchema,
  handler: async args => {
    const chord = parseChord(args.chord)
    if (!chord) {
      return { ok: false, error: `unknown chord: ${args.chord}` }
    }

    // Explicit key wins when both are supplied — the user's stated intent
    // beats a heuristic inference every time.
    if (args.key) {
      const scale = parseKey(args.key)
      if (!scale) {
        return { ok: false, error: `unknown key: ${args.key}` }
      }
      return {
        ok: true,
        result: {
          analysis: analyzeChord({ chord, scale }),
          analyzedKey: args.key,
        },
      }
    }

    // Fall back to inference from `progression`.
    if (!args.progression || args.progression.length < 2) {
      // Schema refine guards this, but defensive in case the refine is bypassed.
      return { ok: false, error: 'analyze_chord requires either `key` or `progression` (≥2 chords).' }
    }
    const progChords: Chord[] = []
    for (const name of args.progression) {
      const parsed = parseChord(name)
      if (!parsed) {
        return { ok: false, error: `unknown chord in progression: ${name}` }
      }
      progChords.push(parsed)
    }
    const inference = inferKeyFromChords(progChords)
    if (!inference || inference.confidence < MIN_KEY_INFERENCE_CONFIDENCE) {
      const candidates = inference
        ? [{ key: inference.keyName, confidence: inference.confidence }, ...inference.alternates]
        : []
      const candidatesStr = candidates.map(c => `${c.key} (${c.confidence})`).join(', ') || 'none'
      return {
        ok: false,
        error: `cannot infer key with sufficient confidence (need ≥${MIN_KEY_INFERENCE_CONFIDENCE}). Top candidates: ${candidatesStr}. Supply 'key' explicitly or ask the user to confirm.`,
      }
    }
    return {
      ok: true,
      result: {
        analysis: analyzeChord({ chord, scale: inference.scale }),
        analyzedKey: inference.keyName,
        keyInference: {
          confidence: inference.confidence,
          alternates: inference.alternates,
        },
      },
    }
  },
})

// 2. chord_from_name — parse a chord name into a structured chord
const chordFromNameSchema = z.object({
  name: chordNameArg,
})

export const createChordFromNameTool = (): Tool<z.infer<typeof chordFromNameSchema>, ChordProjection | null> => ({
  name: 'chord_from_name',
  description:
    'Parse a chord name (e.g., "Cmaj7", "G7", "Am") into its root, variant, and component notes. Returns null when the name does not parse.',
  schema: chordFromNameSchema,
  handler: async args => {
    const chord = parseChord(args.name)
    return { ok: true, result: projectChord(chord) }
  },
})

// 3. chord_from_notes — best-match chord candidates for a set of notes
const chordFromNotesSchema = z.object({
  notes: noteNameArrayArg,
})

export const createChordFromNotesTool = (): Tool<z.infer<typeof chordFromNotesSchema>, ChordProjection[]> => ({
  name: 'chord_from_notes',
  description:
    'Given a set of notes, return the chord-variant candidates that match. Notes may include octave (e.g., "C4") to enable bass-note detection. Returns an empty array when no chord matches.',
  schema: chordFromNotesSchema,
  handler: async args => {
    // ChordLib.fromNotes accepts NoteName | NoteOctave — accept the raw strings
    // and let it filter.
    const candidates = ChordLib.fromNotes(args.notes as never)
    const projected = candidates
      .map(c => projectChord(c as Chord))
      .filter((c): c is ChordProjection => c !== null)
    return { ok: true, result: projected }
  },
})

// 4. transpose_chord — return the transposed chord name string
const transposeChordSchema = z.object({
  chord: chordNameArg,
  semitones: z
    .number()
    .int()
    .gte(-24)
    .lte(24)
    .describe('Number of semitones to transpose up (positive) or down (negative). Capped at ±24.'),
})

export const createTransposeChordTool = (): Tool<z.infer<typeof transposeChordSchema>, ChordProjection | null> => ({
  name: 'transpose_chord',
  description: 'Transpose a chord by a number of semitones. Returns the new chord projection, or null when the chord name does not parse.',
  schema: transposeChordSchema,
  handler: async args => {
    const chord = parseChord(args.chord)
    if (!chord) {
      return { ok: false, error: `unknown chord: ${args.chord}` }
    }
    const transposed = ChordLib.transpose(chord, args.semitones)
    return { ok: true, result: projectChord(transposed) }
  },
})

// 5. get_scale — notes and intervals for a scale
const getScaleSchema = z.object({
  key: keyArg,
})

interface ScaleInfo {
  root: NoteName
  type: ScaleType
  notes: NoteName[]
  intervals: string[]
}

export const createGetScaleTool = (): Tool<z.infer<typeof getScaleSchema>, ScaleInfo> => ({
  name: 'get_scale',
  description:
    'Get the seven distinct pitch classes and degree intervals of a scale. Example: `key: "F lydian"` → notes F, G, A, B, C, D, E with intervals I, II, III, #IV, V, VI, VII. Interval labels use ASCII flat/sharp prefixes (e.g., `bIII`, `#IV`) to indicate altered scale degrees in modal or minor contexts.',
  schema: getScaleSchema,
  handler: async args => {
    const scale = parseKey(args.key)
    if (!scale) {
      return { ok: false, error: `unknown key: ${args.key}` }
    }
    // music-lib's `getNotes` includes the octave restatement (VIII) so the
    // 8-entry intervals array aligns 1:1 with notes for diatonic-chord lookup.
    // The LLM cares about the distinct pitch classes; drop the duplicate root
    // at the tail when it's there.
    const rawNotes = ScaleLib.getNotes(scale)
    const notes = rawNotes.length > 1 && rawNotes[rawNotes.length - 1] === rawNotes[0]
      ? rawNotes.slice(0, -1)
      : rawNotes
    const intervals = (ScaleLib.getIntervals(scale) as string[]).filter(i => i !== 'VIII')
    return {
      ok: true,
      result: {
        root: scale.root,
        type: scale.type ?? 'major',
        notes,
        intervals,
      },
    }
  },
})

// 6. get_diatonic_chords — the chord built on each scale degree
const getDiatonicChordsSchema = z.object({
  key: keyArg,
})

interface DiatonicChord {
  degree: string
  chord: ChordProjection | null
}

export const createGetDiatonicChordsTool = (): Tool<z.infer<typeof getDiatonicChordsSchema>, DiatonicChord[]> => ({
  name: 'get_diatonic_chords',
  description:
    'Return the seven diatonic chords of a key, one per scale degree (I through VII). Each entry includes the degree label and the chord projection (root, variant, quality, seventh, notes). Degree labels use ASCII flat/sharp prefixes (`bIII`, `bVI`, `bVII` in minor keys; `#IV` in lydian) to mark altered scale degrees.',
  schema: getDiatonicChordsSchema,
  handler: async args => {
    const scale = parseKey(args.key)
    if (!scale) {
      return { ok: false, error: `unknown key: ${args.key}` }
    }
    const chords = ScaleLib.getChords(scale)
    const intervals = ScaleLib.getIntervals(scale)
    // `getChords` returns one entry per scale-degree (filtered through Boolean).
    // Align them with the interval labels at the same indexes.
    const result: DiatonicChord[] = chords.map((chord, i) => ({
      degree: (intervals[i] as string) ?? `degree-${i + 1}`,
      chord: projectChord(chord as Chord),
    }))
    return { ok: true, result }
  },
})

// 7. get_diatonic_function — the Roman-numeral function of a chord in a key
const getDiatonicFunctionSchema = z.object({
  chord: chordNameArg,
  key: keyArg,
})

export const createGetDiatonicFunctionTool = (): Tool<
  z.infer<typeof getDiatonicFunctionSchema>,
  ChordFunction | null
> => ({
  name: 'get_diatonic_function',
  description:
    'Return the Roman-numeral function of a chord against a key (e.g., "I", "ii", "V", "vii°"). Returns null when the chord is not diatonic to the scale.',
  schema: getDiatonicFunctionSchema,
  handler: async args => {
    const chord = parseChord(args.chord)
    if (!chord) {
      return { ok: false, error: `unknown chord: ${args.chord}` }
    }
    const scale = parseKey(args.key)
    if (!scale) {
      return { ok: false, error: `unknown key: ${args.key}` }
    }
    return { ok: true, result: getDiatonicFunction(chord, scale) }
  },
})

// 8. get_secondary_dominant — V/x label when the chord is a secondary dominant
const getSecondaryDominantSchema = z.object({
  chord: chordNameArg,
  key: keyArg,
})

interface SecondaryDominantResult {
  chordFunction: ChordFunction
  borrowing: 'secondary-dominant'
}

export const createGetSecondaryDominantTool = (): Tool<
  z.infer<typeof getSecondaryDominantSchema>,
  SecondaryDominantResult | null
> => ({
  name: 'get_secondary_dominant',
  description:
    'Return the secondary-dominant label (e.g., "V/ii", "V/IV", "V/V", "V/vi") for a chord against a major key, or null when the chord is not a secondary dominant. **Major keys only in v1** — minor-key inputs always return null even when a secondary dominant exists (e.g., A7 → Dm in C minor). Use `analyze_chord` for minor-key analysis instead.',
  schema: getSecondaryDominantSchema,
  handler: async args => {
    const chord = parseChord(args.chord)
    if (!chord) {
      return { ok: false, error: `unknown chord: ${args.chord}` }
    }
    const scale = parseKey(args.key)
    if (!scale) {
      return { ok: false, error: `unknown key: ${args.key}` }
    }
    const raw = getSecondaryDominantFunction(chord, scale)
    if (!raw) {
      return { ok: true, result: null }
    }
    // Coerce music-lib's ChordBorrowing to the literal we surface (it's always
    // `'secondary-dominant'` for this code path).
    return {
      ok: true,
      result: { chordFunction: raw.chordFunction, borrowing: 'secondary-dominant' },
    }
  },
})

// 9. get_tritone_sub — subV/x label when the chord is a tritone substitution
const getTritoneSubSchema = z.object({
  chord: chordNameArg,
  next: chordNameArg.describe(
    'The chord this one resolves to. The substitution rule is defined relative to the resolution target.'
  ),
  key: keyArg,
})

export const createGetTritoneSubTool = (): Tool<
  z.infer<typeof getTritoneSubSchema>,
  ChordFunction | null
> => ({
  name: 'get_tritone_sub',
  description:
    'Return the tritone-sub label (e.g., "subV/I", "subV/V") for a chord that resolves to `next`, against a key. Returns null when the relationship does not hold.',
  schema: getTritoneSubSchema,
  handler: async args => {
    const chord = parseChord(args.chord)
    if (!chord) {
      return { ok: false, error: `unknown chord: ${args.chord}` }
    }
    const next = parseChord(args.next)
    if (!next) {
      return { ok: false, error: `unknown chord (next): ${args.next}` }
    }
    const scale = parseKey(args.key)
    if (!scale) {
      return { ok: false, error: `unknown key: ${args.key}` }
    }
    return { ok: true, result: getTritoneSubFunction(chord, scale, next) }
  },
})

// 10. summarize_progression — one-line mood descriptor for a progression
const summarizeProgressionSchema = z.object({
  chords: z
    .array(chordNameArg)
    .min(1)
    .describe('Ordered chord names in the progression.'),
  key: keyArg,
})

export const createSummarizeProgressionTool = (): Tool<
  z.infer<typeof summarizeProgressionSchema>,
  string | null
> => ({
  name: 'summarize_progression',
  description:
    'Return a one-line mood descriptor for a chord progression (e.g., "Diatonic major — optimistic, declarative.", "Blues — hard-luck, defiant, sensual.", "Secondary dominants — drama, build, payoff."). Useful for grounding lyric or section suggestions in the progression\'s color. If the song has no chord progression yet, ASK the user — do not call this tool with an empty chord list.',
  schema: summarizeProgressionSchema,
  handler: async args => {
    const scale = parseKey(args.key)
    if (!scale) {
      return { ok: false, error: `unknown key: ${args.key}` }
    }
    // Pre-compute the function tag for each chord. We do NOT fall back to
    // 'chromatic' on parse failure: conflating *unparseable garbage* with
    // *deliberate chromatic motion* would corrupt the mood signal. Error out
    // so the LLM either fixes the input or asks the user.
    const functionTags: ChordFunction[] = []
    for (const name of args.chords) {
      const chord = parseChord(name)
      if (!chord) {
        return { ok: false, error: `unknown chord: ${name}` }
      }
      const analysis = analyzeChord({ chord, scale })
      // analyzeChord legitimately yields 'chromatic' for chords outside the key —
      // that's a real musical tag and the summarizer's priority order handles it.
      functionTags.push(analysis?.chordFunction ?? 'chromatic')
    }
    const summary = summarizeProgression({ functionTags, scaleType: scale.type })
    return { ok: true, result: summary }
  },
})

/**
 * All 10 music-lib bridge tools. Exposed as an array for one-call registration
 * via `createRegistry`.
 */
export const createMusicLibBridgeTools = (): AnyTool[] => [
  createAnalyzeChordTool(),
  createChordFromNameTool(),
  createChordFromNotesTool(),
  createTransposeChordTool(),
  createGetScaleTool(),
  createGetDiatonicChordsTool(),
  createGetDiatonicFunctionTool(),
  createGetSecondaryDominantTool(),
  createGetTritoneSubTool(),
  createSummarizeProgressionTool(),
]
